import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import Breadcrumb from "../../Components/Breadcrumb";
import axiosInstance from "../../axiosInterceptor";
import { toast } from "react-toastify";

const G = {
  primary: "#003C1F",
  hover: "#00542C",
  soft: "#E8F5EE",
  mid: "#1a7a4a",
  midSoft: "#d0edd9",
  drag: "#00874a",
  dragSoft: "#edfaf3",
  border: "#b6d9c5",
  textMuted: "#5a7a66",
  disabledBg: "#c8e6d4",
  errorBorder: "#a8d5b5",
  errorBg: "#f0faf4",
  errorText: "#1e5c38",
};

// ── drag-drop upload box ──────────────────────────────────────────────────────
const UploadBox = ({ label, accept, file, onFile, disabled }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFile(dropped);
  };

  return (
    <div
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? G.drag : G.border}`,
        borderRadius: "12px",
        // ✅ FIX 1: white background always, light green only while dragging
        background: dragging ? G.dragSoft : "#ffffff",
        padding: "40px 24px",
        textAlign: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "border-color 0.2s, background 0.2s",
        minHeight: "190px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files?.[0])}
        disabled={disabled}
      />

      <svg
        width="52"
        height="52"
        viewBox="0 0 24 24"
        fill="none"
        stroke={dragging ? G.drag : G.mid}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      </svg>

      {file ? (
        <div>
          <p
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: G.mid,
              margin: 0,
            }}
          >
            {file.name}
          </p>
          <p
            style={{ fontSize: "12px", color: G.textMuted, margin: "4px 0 0" }}
          >
            {(file.size / 1024 / 1024).toFixed(2)} MB — click to change
          </p>
        </div>
      ) : (
        <div>
          <p
            style={{
              fontSize: "14px",
              fontWeight: 500,
              color: G.primary,
              margin: 0,
            }}
          >
            {label}
          </p>
          <p
            style={{ fontSize: "12px", color: G.textMuted, margin: "4px 0 0" }}
          >
            or click to browse
          </p>
        </div>
      )}
    </div>
  );
};

// ── action button — solid green ───────────────────────────────────────────────
const ActionBtn = ({ label, onClick, loading, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    style={{
      width: "100%",
      marginTop: "16px",
      padding: "10px",
      borderRadius: "8px",
      border: `1.5px solid ${G.primary}`,
      background: G.primary,
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "background 0.18s, color 0.18s",
    }}
    onMouseEnter={(e) => {
      if (!disabled && !loading) {
        e.currentTarget.style.background = G.hover; // slightly lighter on hover
        e.currentTarget.style.color = "#fff";
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = G.hover; // slightly lighter on hover
      e.currentTarget.style.color = "#fff";
    }}
  >
    {loading ? (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        <span className="spinner-border spinner-border-sm" role="status" />
        Uploading…
      </span>
    ) : (
      label
    )}
  </button>
);

// ── result feedback ───────────────────────────────────────────────────────────
const ResultBox = ({ result, type }) => {
  if (!result) return null;
  const ok = result.isSuccess;
  return (
    <div
      style={{
        marginTop: "12px",
        padding: "11px 14px",
        borderRadius: "8px",
        background: ok ? G.soft : G.errorBg,
        border: `1px solid ${ok ? G.midSoft : G.errorBorder}`,
        fontSize: "13px",
        color: ok ? G.primary : G.errorText,
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>{result.message}</p>

      {type === "zip" && ok && (
        <p style={{ margin: "5px 0 0", fontSize: "12px", color: G.textMuted }}>
          ✓ {result.totalExtracted} image(s) extracted
          {result.skipped > 0 &&
            ` · ${result.skipped} non-image file(s) skipped`}
        </p>
      )}

      {type === "excel" && ok && (
        <div
          style={{
            marginTop: "5px",
            display: "flex",
            gap: "16px",
            fontSize: "12px",
            color: G.textMuted,
          }}
        >
          <span>
            Created:{" "}
            <strong style={{ color: G.primary }}>
              {result.results?.created ?? 0}
            </strong>
          </span>
          <span>
            Updated:{" "}
            <strong style={{ color: G.primary }}>
              {result.results?.updated ?? 0}
            </strong>
          </span>
          {result.results?.skipped > 0 && (
            <span>
              Skipped:{" "}
              <strong style={{ color: G.primary }}>
                {result.results.skipped}
              </strong>
            </span>
          )}
        </div>
      )}

      {result.results?.errors?.length > 0 && (
        <details style={{ marginTop: "8px" }}>
          <summary
            style={{
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              color: G.primary,
            }}
          >
            View {result.results.errors.length} error(s)
          </summary>
          <div
            style={{ marginTop: "6px", maxHeight: "100px", overflowY: "auto" }}
          >
            {result.results.errors.map((e, i) => (
              <p
                key={i}
                style={{
                  margin: "2px 0",
                  fontSize: "11px",
                  color: G.errorText,
                }}
              >
                Row {e.row}: {e.reason}
              </p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// ── main component ────────────────────────────────────────────────────────────
const ImportExportProduct = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [excelResult, setExcelResult] = useState(null);
  const [zipResult, setZipResult] = useState(null);

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "Products", path: "/products" },
    { label: "Import / Export", path: "*" },
  ];

  // ── download sample excel ──────────────────────────────────────────────
  const handleSampleDownload = () => {
    const headers = [
      "Sort Order",
      "Product Title",
      "Product SKU",
      "Product Link",
      "Description",
      "Category",
      "Country IDs",
      "Quality",
      "Source",
      "Default Pack Size",
      "Thumbnail Images",
      "Meta Title",
      "Meta Keywords",
      "Meta Description",
      "Is Active",
    ];

    const sampleRows = [
      [
        "1",
        "Arabica Dark Roast Whole Bean Coffee",
        "ARB-DR-002",
        "arabica-dark-roast-whole-bean-coffee",
        "100% Arabica dark roast whole beans sourced from the highlands of Ethiopia.",
        "Roasted Coffee",
        "UAE",
        "Deluxe",
        "Single Region",
        "4g",
        "admin/product/product01.jpg",
        "Arabica Dark Roast Whole Bean Coffee",
        "arabica, dark roast, whole bean, coffee, UAE",
        "Premium 100% Arabica dark roast whole bean coffee from UAE.",
        "FALSE",
      ],
      [
        "2",
        "Yemen Haraaz Green Coffee Beans",
        "YEM-HR-001",
        "yemen-haraaz-green-coffee-beans",
        "Premium green coffee beans from the Haraaz region of Yemen.",
        "Green Coffee",
        "Yemen",
        "Classic",
        "Single Subregion",
        "8g",
        "admin/product/product01.jpg, admin/product/product1.jpg",
        "Yemen Haraaz Green Coffee Beans",
        "green coffee, haraaz, yemen, unroasted",
        "Premium green coffee beans from Yemen Haraaz region.",
        "TRUE",
      ],
    ];

    const wsData = [headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = headers.map((h, i) => ({
      wch:
        Math.max(
          h.length,
          ...sampleRows.map((r) => String(r[i] ?? "").length),
        ) + 4,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "product_import_sample.xlsx");
    toast.success("Sample file downloaded.");
  };

  // ── export ─────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axiosInstance.get("/importExport/exportProducts", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `products_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Products exported successfully.");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // ── upload zip ─────────────────────────────────────────────────────────
  const handleZipUpload = async () => {
    if (!zipFile) return toast.warn("Please select a .zip file first.");
    const form = new FormData();
    form.append("images", zipFile);
    setZipLoading(true);
    setZipResult(null);
    try {
      const res = await axiosInstance.post(
        "/importExport/uploadProductImagesZip",
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setZipResult(res.data);
      if (res.data.isSuccess) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.message || "ZIP upload failed.";
      setZipResult({ isSuccess: false, message: msg });
      toast.error(msg);
    } finally {
      setZipLoading(false);
    }
  };

  // ── import excel ───────────────────────────────────────────────────────
  const handleExcelUpload = async () => {
    if (!excelFile) return toast.warn("Please select an Excel file first.");
    const form = new FormData();
    form.append("file", excelFile);
    setExcelLoading(true);
    setExcelResult(null);
    try {
      const res = await axiosInstance.post(
        "/importExport/importProducts",
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setExcelResult(res.data);
      if (res.data.isSuccess) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.message || "Import failed.";
      setExcelResult({ isSuccess: false, message: msg });
      toast.error(msg);
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <div className="content-wrapper">
      {/* page header */}
      <div className="page-header mb-3">
        <h3 className="custom-page-title">Import / Export Products</h3>
        <Breadcrumb items={breadcrumbdata} />
      </div>

      {/* ── export + sample bar ── */}
      <div
        className="d-flex justify-content-between align-items-center bg-white p-2 mt-n2 flex-wrap mb-3"
        style={{ gap: "12px" }}
      >
        <span style={{ fontSize: "13px" }}>
          Download all current products as an Excel file
        </span>

        {/* ✅ FIX 3: explicit gap between the two bar buttons */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {/* Download Sample File */}
          <button
            onClick={handleSampleDownload}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              borderRadius: "6px",
              border: `1.5px solid ${G.mid}`,
              background: G.soft,
              color: G.mid,
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              transition: "background 0.18s, color 0.18s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = G.mid;
              e.currentTarget.style.color = "#fff";
              e.currentTarget.querySelector("svg").style.stroke = "#fff"; // ✅ icon turns white on hover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = G.soft;
              e.currentTarget.style.color = G.mid;
              e.currentTarget.querySelector("svg").style.stroke = G.mid; // ✅ icon back to green
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke={G.mid} // ✅ green by default, not currentColor
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transition: "stroke 0.18s" }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Sample File
          </button>

          {/* Export Products */}
          <button
            onClick={handleExport}
            disabled={exportLoading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              borderRadius: "6px",
              border: `1.5px solid ${G.primary}`,
              background: "#fff",
              color: G.primary,
              fontWeight: 600,
              fontSize: "13px",
              cursor: exportLoading ? "not-allowed" : "pointer",
              transition: "background 0.18s, color 0.18s",
            }}
            onMouseEnter={(e) => {
              if (!exportLoading) {
                e.currentTarget.style.background = G.primary;
                e.currentTarget.style.color = "#fff";
                e.currentTarget.querySelector("svg").style.stroke = "#fff"; // ✅ icon turns white on hover
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = G.primary;
              e.currentTarget.querySelector("svg").style.stroke = G.primary; // ✅ icon back to green
            }}
          >
            {exportLoading ? (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span className="spinner-border spinner-border-sm" />
                Exporting…
              </span>
            ) : (
              <>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={G.primary} // ✅ dark green by default
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ transition: "stroke 0.18s" }}
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
                Export Products
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── two upload cards ── */}
      <div className="row">
        {/* Excel import */}
        <div className="col-md-6 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h6
                className="fw-bold mb-3"
                style={{ fontSize: "15px", color: G.primary }}
              >
                Import Excel
              </h6>
              <UploadBox
                label="Drag & Drop your Excel file here"
                accept=".xlsx,.xls"
                file={excelFile}
                onFile={setExcelFile}
                disabled={excelLoading}
              />
              <ActionBtn
                label="Upload"
                onClick={handleExcelUpload}
                loading={excelLoading}
                disabled={!excelFile}
              />
              <ResultBox result={excelResult} type="excel" />
            </div>
          </div>
        </div>

        {/* ZIP upload */}
        <div className="col-md-6 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h6
                className="fw-bold mb-3"
                style={{ fontSize: "15px", color: G.primary }}
              >
                Upload Image ZIP
              </h6>
              <UploadBox
                label="Drag & Drop ZIP file here"
                accept=".zip"
                file={zipFile}
                onFile={setZipFile}
                disabled={zipLoading}
              />
              <ActionBtn
                label="Upload ZIP"
                onClick={handleZipUpload}
                loading={zipLoading}
                disabled={!zipFile}
              />
              <ResultBox result={zipResult} type="zip" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExportProduct;
